import type { ApplicationConfigType, ApplicationItemType, FormDataType } from './types';
import { collectApplicationFiles, type ExportFile } from './applicationFormData';

/**
 * Eksport av en søknad til PDF og ZIP, sett fra søkerens side.
 *
 * `@react-pdf/renderer` og `jszip` er tunge og lastes derfor med dynamisk
 * import — de skal ikke ligge i hovedbundelen for alle som bare åpner en
 * søknad. Begge funksjonene under er derfor async hele veien.
 */

type PdfInput = {
    application: ApplicationItemType;
    applicationConfig: ApplicationConfigType | null;
    formData: FormDataType;
    statusLabel: string;
    t: (key: string) => string;
};

/** Filnavn uten tegn som er ulovlige i Windows/macOS-filsystemer. */
export const safeFileName = (value: string) =>
    value.replace(/[/\\:*?"<>|]/g, '-').replace(/\s+/g, ' ').trim();

export const applicationBaseName = (application: ApplicationItemType) =>
    safeFileName(`${application.id} - ${application.name ?? 'soknad'}`);

/** Renderer søknaden til en PDF-blob. */
export async function buildApplicationPdfBlob(input: PdfInput): Promise<Blob> {
    const [{ pdf }, { default: ApplicationPdf }, { createElement }] = await Promise.all([
        import('@react-pdf/renderer'),
        import('../pdf/ApplicationPdf'),
        import('react'),
    ]);

    return pdf(createElement(ApplicationPdf, input)).toBlob();
}

/** Trigger nedlasting av en blob i nettleseren. */
export function saveBlob(blob: Blob, fileName: string) {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    // Revoke først etter at klikket er behandlet — Safari avbryter
    // nedlastingen hvis object-URL-en frigis synkront.
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

export type ZipResult = {
    /** Antall vedlegg som faktisk havnet i arkivet. */
    included: number;
    /** Vedlegg vi ikke fikk hentet — arkivet får en lenkefil for disse i stedet. */
    failed: ExportFile[];
};

/**
 * Pakker søknaden som ZIP: PDF-en + vedleggene søker har tilgang til.
 *
 * Vedleggene ligger bak MODX' proxy-download-ressurs på API-hosten, og
 * søkerportalen kjører på et annet origin i alt annet enn lokal utvikling.
 * Å lese bytene i JS krever derfor at ressursen sender CORS-header. Er den
 * ikke på plass, feiler `fetch` per fil — da lager vi ikke et halvt arkiv i
 * stillhet, men legger ved `Vedlegg-lenker.txt` med direkte nedlastingslenker
 * og rapporterer tilbake hva som manglet, slik at knappen fortsatt gir
 * brukeren noe brukbart.
 */
export async function buildApplicationZipBlob(
    input: PdfInput,
    onProgress?: (done: number, total: number) => void,
): Promise<{ blob: Blob; result: ZipResult }> {
    const [{ default: JSZip }, pdfBlob] = await Promise.all([
        import('jszip'),
        buildApplicationPdfBlob(input),
    ]);

    const { application, applicationConfig } = input;
    const baseName = applicationBaseName(application);
    const zip = new JSZip();

    zip.file(`${baseName}.pdf`, pdfBlob);

    const files = collectApplicationFiles(application, applicationConfig);
    const apiBase = import.meta.env.VITE_API_BASE_URL ?? '';
    const failed: ExportFile[] = [];
    const usedNames = new Set<string>([`${baseName}.pdf`.toLowerCase()]);
    let included = 0;
    let done = 0;

    for (const file of files) {
        try {
            const response = await fetch(apiBase + file.url);
            if (!response.ok) throw new Error(`HTTP ${response.status}`);

            // Unngå at to vedlegg med samme filnavn overskriver hverandre.
            let name = safeFileName(file.name);
            if (usedNames.has(name.toLowerCase())) {
                const dot = name.lastIndexOf('.');
                const stem = dot > 0 ? name.slice(0, dot) : name;
                const ext = dot > 0 ? name.slice(dot) : '';
                let counter = 2;
                while (usedNames.has(`${stem} (${counter})${ext}`.toLowerCase())) counter++;
                name = `${stem} (${counter})${ext}`;
            }
            usedNames.add(name.toLowerCase());

            zip.file(`Vedlegg/${name}`, await response.blob());
            included++;
        } catch {
            failed.push(file);
        }
        onProgress?.(++done, files.length);
    }

    if (failed.length > 0) {
        const lines = [
            'Disse vedleggene kunne ikke pakkes automatisk.',
            'Åpne lenkene under mens du er innlogget for å laste dem ned:',
            '',
            ...failed.map((file) => `${file.name}\n${apiBase}${file.url}\n`),
        ];
        zip.file('Vedlegg-lenker.txt', lines.join('\n'));
    }

    const blob = await zip.generateAsync({ type: 'blob' });
    return { blob, result: { included, failed } };
}
